import { NextApiRequest, NextApiResponse } from 'next';
import { createClient } from '@supabase/supabase-js';

type UserRole = 'admin' | 'asesor' | 'cliente';

interface AuthenticatedUser {
  id: string;
  email: string;
  roles: UserRole[];
  primaryRole: UserRole;
}

type AuthenticatedHandler = (
  req: NextApiRequest,
  res: NextApiResponse,
  user: AuthenticatedUser
) => Promise<void | NextApiResponse>;

/**
 * S1.3: Global authentication middleware for API routes.
 * Validates JWT token, fetches user roles, and enforces RBAC.
 * 
 * Usage:
 *   export default withAuth(handler);
 *   export default withAuth(handler, ['admin', 'asesor']);
 */
export function withAuth(
  handler: AuthenticatedHandler,
  allowedRoles?: UserRole[]
) {
  return async (req: NextApiRequest, res: NextApiResponse) => {
    try {
      const supabaseUrl = process.env.SUPABASE_URL || process.env.NEXT_PUBLIC_SUPABASE_URL;
      const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !supabaseAnonKey) {
        console.error('[apiAuth] Missing Supabase environment variables');
        return res.status(500).json({ error: 'Server configuration error' });
      }

      // Extract token from Authorization header or cookie
      const authHeader = req.headers.authorization;
      let token: string | undefined;

      if (authHeader?.startsWith('Bearer ')) {
        token = authHeader.substring(7);
      }

      // Fallback: try to get token from Supabase auth cookie
      if (!token) {
        const cookies = req.headers.cookie || '';
        const sbAccessToken = cookies
          .split(';')
          .find(c => c.trim().startsWith('sb-') && c.includes('-auth-token'));
        if (sbAccessToken) {
          try {
            const cookieValue = decodeURIComponent(sbAccessToken.split('=').slice(1).join('='));
            const parsed = JSON.parse(cookieValue);
            token = parsed?.access_token || parsed?.[0]?.access_token;
          } catch {
            // Cookie parsing failed, continue without token
          }
        }
      }

      if (!token) {
        return res.status(401).json({ error: 'No autorizado. Token de autenticación requerido.' });
      }

      // Verify token with Supabase
      const supabase = createClient(supabaseUrl, supabaseAnonKey, {
        global: { headers: { Authorization: `Bearer ${token}` } },
      });

      const { data: { user }, error: authError } = await supabase.auth.getUser(token);

      if (authError || !user) {
        return res.status(401).json({ error: 'Token inválido o expirado.' });
      }

      // Fetch user profile and roles
      const { data: profile } = await supabase
        .from('profiles')
        .select('rol')
        .eq('id', user.id)
        .single();

      const { data: userRoles } = await supabase
        .from('user_roles')
        .select('rol')
        .eq('user_id', user.id);

      const roles: UserRole[] = [];
      if (profile?.rol) roles.push(profile.rol as UserRole);
      if (userRoles) {
        for (const ur of userRoles) {
          if (!roles.includes(ur.rol as UserRole)) {
            roles.push(ur.rol as UserRole);
          }
        }
      }

      if (roles.length === 0) {
        roles.push('cliente'); // Default role
      }

      // Check role authorization
      if (allowedRoles && allowedRoles.length > 0) {
        const hasPermission = roles.some(r => allowedRoles.includes(r));
        if (!hasPermission) {
          return res.status(403).json({
            error: 'No tienes permisos para realizar esta acción.',
          });
        }
      }

      const authenticatedUser: AuthenticatedUser = {
        id: user.id,
        email: user.email || '',
        roles,
        primaryRole: roles[0],
      };

      return handler(req, res, authenticatedUser);
    } catch (error: any) {
      console.error('[apiAuth] Unexpected error:', error);
      return res.status(500).json({ error: 'Error interno del servidor.' });
    }
  };
}

/**
 * Rate limiting helper - simple in-memory rate limiter.
 * For production, use Redis or similar.
 */
const rateLimitMap = new Map<string, { count: number; resetAt: number }>();

export function rateLimit(
  identifier: string,
  maxRequests: number = 60,
  windowMs: number = 60000
): boolean {
  const now = Date.now();
  const entry = rateLimitMap.get(identifier);

  if (!entry || now > entry.resetAt) {
    rateLimitMap.set(identifier, { count: 1, resetAt: now + windowMs });
    return true;
  }

  if (entry.count >= maxRequests) {
    return false;
  }

  entry.count++;
  return true;
}

/**
 * Wraps withAuth + rate limiting.
 */
export function withAuthAndRateLimit(
  handler: AuthenticatedHandler,
  allowedRoles?: UserRole[],
  maxRequests: number = 60
) {
  return withAuth(async (req, res, user) => {
    const identifier = `${user.id}:${req.url}`;
    if (!rateLimit(identifier, maxRequests)) {
      return res.status(429).json({
        error: 'Demasiadas solicitudes. Por favor, espera un momento.',
      });
    }
    return handler(req, res, user);
  }, allowedRoles);
}
